import { apiService } from './api';

export interface Portfolio {
  id: number;
  name: string;
  description?: string;
  transaction_count: number;
  created_at: string;
  transactions: Transaction[];
}

export interface Transaction {
  id: number;
  portfolio_id: number;
  stock_symbol: string;
  stock_name: string;
  transaction_type: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  transaction_date: string;
  source: 'MANUAL' | 'HISTORIC' | 'UPLOADED';
  created_at: string;
}

export interface CreatePortfolioData {
  name: string;
  description?: string;
}

export interface CreateTransactionData {
  portfolio_id: number;
  stock_symbol: string;
  stock_name: string;
  transaction_type: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  transaction_date: string;
}

export interface PortfolioSummary {
  total_value: number;
  total_investment: number;
  total_shares?: number;
  portfolio_average_price?: number;
  real_investment_data?: {
    total_nominal_investment: number;
    total_real_investment: number;
    cost_of_money_impact: number;
    annual_rate_used: number;
    calculation_date: string;
  };
  profit_loss: number;
  profit_loss_percentage: number;
  top_performers: Array<{
    symbol: string;
    name: string;
    profit_loss: number;
    profit_loss_percentage: number;
  }>;
}

export interface Holding {
  symbol: string;
  name: string;
  isin: string;
  quantity: number;
  invested_amount: number;
  average_price: number;
  real_investment: number;
  real_average_price: number;
  last_transaction_date: string;
  buy_transactions: number;
  sell_transactions: number;
  bonus_transactions: number;
  total_transactions: number;
}

export interface HoldingsResponse {
  portfolio: Portfolio;
  holdings: Holding[];
  total_holdings: number;
  total_invested: number;
  total_quantity: number;
}

export interface StockTransaction {
  id: number;
  transaction_type: 'BUY' | 'SELL' | 'BONUS' | 'OPEN_POSITION';
  quantity: number;
  price: number;
  total_value: number;
  real_price?: number;
  cost_of_money_impact?: number;
  transaction_date: string;
  source: 'MANUAL' | 'HISTORIC' | 'UPLOADED';
  source_file: string;
  notes: string;
  created_at: string;
}

export interface StockTransactionsResponse {
  stock: {
    symbol: string;
    name: string;
    exchange: string;
  };
  current_holding: {
    quantity: number;
    invested_amount: number;
    average_price: number;
  };
  transactions: StockTransaction[];
  total_transactions: number;
}

export interface PortfolioTransactions {
  portfolio_id: number;
  portfolio_name: string;
  transactions: StockTransaction[];
  quantity: number;
  invested_amount: number;
}

export interface StockTransactionsAcrossPortfoliosResponse {
  stock: {
    symbol: string;
    name: string;
    exchange: string;
  };
  portfolios: PortfolioTransactions[];
  total_transactions: number;
  total_quantity: number;
  total_invested: number;
  average_price: number;
}

class PortfolioService {
  async getPortfolios(): Promise<Portfolio[]> {
    const response = await apiService.get<{ portfolios: Portfolio[] }>('/portfolio');
    return response.portfolios;
  }

  async getPortfolio(id: number): Promise<Portfolio> {
    const response = await apiService.get<{ portfolio: Portfolio; transactions: Transaction[] }>(`/portfolio/${id}`);
    return { ...response.portfolio, transactions: response.transactions };
  }

  async createPortfolio(data: CreatePortfolioData): Promise<Portfolio> {
    const response = await apiService.post<{ message: string; portfolio: Portfolio }>('/portfolio', data);
    return response.portfolio;
  }

  async updatePortfolio(id: number, data: Partial<CreatePortfolioData>): Promise<Portfolio> {
    const response = await apiService.put<{ message: string; portfolio: Portfolio }>(`/portfolio/${id}`, data);
    return response.portfolio;
  }

  async deletePortfolio(id: number): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/portfolio/${id}`);
  }

  async getTransactions(portfolioId: number): Promise<Transaction[]> {
    return apiService.get<Transaction[]>(`/portfolio/${portfolioId}/transactions`);
  }

  async addTransaction(data: CreateTransactionData): Promise<Transaction> {
    const response = await apiService.post<{ message: string; transaction: Transaction }>(`/portfolio/${data.portfolio_id}/transactions`, data);
    return response.transaction;
  }

  async updateTransaction(portfolioId: number, transactionId: number, data: Partial<CreateTransactionData>): Promise<Transaction> {
    const response = await apiService.put<{ message: string; transaction: Transaction }>(`/portfolio/${portfolioId}/transactions/${transactionId}`, data);
    return response.transaction;
  }

  async deleteTransaction(portfolioId: number, transactionId: number): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/portfolio/${portfolioId}/transactions/${transactionId}`);
  }

  async uploadTransactions(portfolioId: number, file: File): Promise<{
    message: string;
    new_transactions: number;
    duplicate_skipped: number;
    replaced_positions: number;
    filename: string;
    total_parsed: number;
    file_type: 'open_positions' | 'transactions';
  }> {
    return apiService.uploadFile<{
      message: string;
      new_transactions: number;
      duplicate_skipped: number;
      replaced_positions: number;
      filename: string;
      total_parsed: number;
      file_type: 'open_positions' | 'transactions';
    }>(`/portfolio/${portfolioId}/upload`, file);
  }

  async getPortfolioSummary(portfolioId: number): Promise<PortfolioSummary> {
    return apiService.get<PortfolioSummary>(`/portfolio/${portfolioId}/summary`);
  }

  async getPortfolioSummaryWithRate(portfolioId: number, rate: number): Promise<PortfolioSummary> {
    return apiService.get<PortfolioSummary>(`/portfolio/${portfolioId}/summary?cost_of_money=${rate}`);
  }

  async getCurrentValue(portfolioId: number): Promise<any> {
    return apiService.get(`/portfolio/${portfolioId}/current-value`);
  }

  async getAllPortfoliosSummary(): Promise<PortfolioSummary> {
    return apiService.get<PortfolioSummary>('/portfolio/summary');
  }

  async getHoldings(portfolioId: number, costOfMoneyRate?: number): Promise<HoldingsResponse> {
    const queryParam = costOfMoneyRate !== undefined ? `?cost_of_money=${costOfMoneyRate}` : '';
    return apiService.get<HoldingsResponse>(`/portfolio/${portfolioId}/holdings${queryParam}`);
  }

  async getStockTransactions(portfolioId: number, stockSymbol: string, costOfMoneyRate?: number): Promise<StockTransactionsResponse> {
    const queryParam = costOfMoneyRate !== undefined ? `?cost_of_money=${costOfMoneyRate}` : '';
    return apiService.get<StockTransactionsResponse>(`/portfolio/${portfolioId}/holdings/${stockSymbol}/transactions${queryParam}`);
  }

  async clearAllTransactions(): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>('/portfolio/clear-all-transactions');
  }

  async getStockTransactionsAcrossPortfolios(stockSymbol: string): Promise<StockTransactionsAcrossPortfoliosResponse> {
    return apiService.get<StockTransactionsAcrossPortfoliosResponse>(`/portfolio/stock/${stockSymbol}/transactions`);
  }

  async getExitedStocks(): Promise<ExitedStocksResponse> {
    return apiService.get<ExitedStocksResponse>('/portfolio/exited-stocks');
  }

  async createReentryTriggers(): Promise<ReentryTriggersResponse> {
    return apiService.post<ReentryTriggersResponse>('/portfolio/create-reentry-triggers', {});
  }

  async getClosedPositions(): Promise<ClosedPositionsResponse> {
    return apiService.get<ClosedPositionsResponse>('/portfolio/closed-positions');
  }

  async getLastTransactionsVsCurrent(): Promise<LastTransactionsVsCurrentResponse> {
    return apiService.get<LastTransactionsVsCurrentResponse>('/portfolio/transactions/last-vs-current');
  }
}

export interface ExitedStock {
  symbol: string;
  name: string;
  quantity: number;
  last_sell_price: number;
  last_sell_date: string;
  reentry_trigger_price: number;
  current_price: number | null;
  below_reentry: boolean;
  price_vs_reentry_pct: number | null;
  avg_sell_price: number;
}

export interface ExitedStocksResponse {
  exited_stocks: ExitedStock[];
  count: number;
}

export interface ReentryTriggersResponse {
  message: string;
  triggers_created: number;
  triggers_skipped: number;
  details: Array<{
    symbol: string;
    status: string;
    trigger_price?: number;
    last_sell_price?: number;
    last_sell_date?: string;
    reason?: string;
    existing_trigger_price?: number;
  }>;
}

export interface ClosedPosition {
  symbol: string;
  name: string;
  total_buy_quantity: number;
  total_buy_value: number;
  avg_buy_price: number;
  total_sell_quantity: number;
  total_sell_value: number;
  avg_sell_price: number;
  realized_profit: number;
  realized_profit_pct: number;
  first_buy_date: string;
  last_sell_date: string;
  last_sell_price: number;
  current_price: number | null;
  reentry_trigger_price: number | null;
  below_reentry: boolean;
  price_vs_reentry_pct: number | null;
  transaction_count: number;
}

export interface ClosedPositionsResponse {
  closed_positions: ClosedPosition[];
  count: number;
  total_realized_profit: number;
}

export interface LastTransactionComparison {
  symbol: string;
  stock_name: string;
  last_tx_type: string;
  last_tx_date: string;
  last_tx_price: number;
  last_tx_quantity: number;
  last_tx_value: number;
  current_price: number | null;
  price_date: string | null;
  price_change: number | null;
  price_change_pct: number | null;
  action_suggestion: 'CONSIDER_REBUY' | 'WATCH_FOR_REBUY' | 'CONSIDER_SELL' | 'WATCH_FOR_SELL' | 'HOLD_OR_AVERAGE' | null;
  is_favorable: boolean;
}

export interface LastTransactionsVsCurrentResponse {
  transactions: LastTransactionComparison[];
  count: number;
  summary: {
    total_buys: number;
    total_sells: number;
    potential_rebuys: number;
    potential_sells: number;
  };
}

export const portfolioService = new PortfolioService(); 